/**
 * Mirrors apps/api/src/modules/store/social-links.ts (same rule, enforced independently on both sides, the convention
 * `normalizeWhatsappNumber` already follows): a social link is an HTTPS URL whose host belongs to that network. A
 * missing scheme is read as https; `http://` or any other scheme is refused, never upgraded.
 */
export const SOCIAL_NETWORKS = [
  'facebook',
  'instagram',
  'tiktok',
  'youtube',
  'x',
  'linkedin',
] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

export const SOCIAL_HOSTS: Readonly<Record<SocialNetwork, readonly string[]>> = {
  facebook: ['facebook.com', 'fb.com', 'fb.me'],
  instagram: ['instagram.com', 'instagr.am'],
  tiktok: ['tiktok.com'],
  youtube: ['youtube.com', 'youtu.be'],
  x: ['x.com', 'twitter.com'],
  linkedin: ['linkedin.com'],
};

/** Example shown as the field's placeholder. */
export const SOCIAL_PLACEHOLDER: Readonly<Record<SocialNetwork, string>> = {
  facebook: 'https://www.facebook.com/ma-boutique',
  instagram: 'https://www.instagram.com/ma-boutique',
  tiktok: 'https://www.tiktok.com/@ma-boutique',
  youtube: 'https://www.youtube.com/@ma-boutique',
  x: 'https://x.com/ma-boutique',
  linkedin: 'https://www.linkedin.com/company/ma-boutique',
};

export function normalizeSocialUrl(network: SocialNetwork, raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '' || /\s/u.test(trimmed)) return null;
  const candidate = /^[a-z][a-z0-9+.-]*:/iu.test(trimmed) ? trimmed : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  if (url.username !== '' || url.password !== '' || url.port !== '') return null;

  const host = url.hostname.toLowerCase();
  if (!SOCIAL_HOSTS[network].some((base) => host === base || host.endsWith(`.${base}`)))
    return null;

  url.hash = '';
  const normalized = url.toString();
  return normalized.length <= 500 ? normalized : null;
}

export const META_PIXEL_ID_PATTERN = /^[0-9]{10,20}$/u;
